import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Plus } from "lucide-react";

// "Sub-Buttons" tab content of ButtonConfigDialog — extracted for readability.
// Purely presentational; all state stays owned by the parent dialog.
export function SubButtonsPanel({
  subButtons, allProducts, setEditingSlot, setSubSearchQuery,
  selectedProduct, buttonImage, setButtonImage, imageUrlInput, setImageUrlInput,
  canGenerateAI, setShowAIDialog, handleSave, onClose, onUploadImage,
}) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-zinc-500">Click a slot to assign a product. Tapping this button on POS opens a popup with these sub-items.</p>

      {/* 5-per-row grid: 5+5+5+1, controls fill the space right of slot 16 */}
      <div className="grid grid-cols-5 gap-2" data-testid="button-config-sub-grid">
        {Array(16).fill(null).map((_, i) => {
          const sb = subButtons.find(s => s.position === i);
          const product = sb ? allProducts.find(p => p.id === sb.product_id) : null;

          return (
            <button
              key={`sub-slot-${i}`}
              data-testid={`button-config-sub-slot-${i}`}
              onClick={() => { setEditingSlot(i); setSubSearchQuery(""); }}
              className={`h-24 rounded-lg border-2 flex flex-col items-center justify-center p-1 transition-all text-center ${
                sb ? "border-zinc-300 bg-white hover:border-blue-300" :
                "border-dashed border-zinc-200 bg-zinc-50 hover:border-zinc-300"
              }`}
            >
              {sb ? (
                <>
                  <span className="text-[9px] font-bold text-zinc-800 line-clamp-2 leading-tight">{sb.label || product?.product_name || product?.name || "..."}</span>
                  {product && <span className="text-[8px] font-mono text-blue-600 mt-0.5">${(product.retail_price || product.price || 0).toFixed(2)}</span>}
                </>
              ) : (
                <span className="text-[9px] text-zinc-400">{i + 1}</span>
              )}
            </button>
          );
        })}

        {/* Image + actions panel beside slot 16 */}
        <div className="col-span-4 h-24 flex flex-col justify-between" data-testid="button-config-sub-panel">
          <div className="flex items-center gap-2">
            <Label className="text-xs whitespace-nowrap">Image {!selectedProduct && subButtons.length > 0 ? "(main button)" : "(optional)"}</Label>
            {buttonImage && (
              <div className="flex items-center gap-1">
                <img src={buttonImage} alt="Button" data-testid="button-config-image-preview" className="w-8 h-8 object-cover rounded border" onError={(e) => { e.target.style.display = "none"; }} />
                <button type="button" className="text-red-500 hover:text-red-700" title="Remove image" onClick={() => setButtonImage("")}><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
            <Input
              placeholder="Paste image URL..."
              data-testid="button-config-image-url"
              value={imageUrlInput}
              onChange={(e) => setImageUrlInput(e.target.value)}
              className="flex-1 h-8 text-xs"
            />
            <Button size="sm" className="h-8 text-xs" data-testid="button-config-set-image-url" disabled={!imageUrlInput.trim()} onClick={() => { setButtonImage(imageUrlInput.trim()); setImageUrlInput(""); }}>
              Set URL
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="button-config-upload-image" onClick={onUploadImage}>
              Upload
            </Button>
            {canGenerateAI && (
              <Button variant="outline" size="sm" className="h-8 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="button-config-generate-ai" onClick={() => setShowAIDialog(true)}>
                <Plus className="w-3.5 h-3.5 mr-1" /> +Image
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              data-testid="button-config-save-subbuttons"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold h-10"
              disabled={!selectedProduct && subButtons.length === 0}
            >
              Save Configuration
            </Button>
            <Button onClick={onClose} variant="outline" className="h-10" data-testid="button-config-cancel">
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
